import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const RESULT_EVENT = 'linea:coupon:result';
const PENDING_KEY = 'linea-pending-coupon';
let activeRequest = null;

const normalizeCode = (value) => String(value || '').trim();

const matchingButtons = (code) =>
  Array.from(document.querySelectorAll('[data-linea-coupon-apply]')).filter(
    (button) => normalizeCode(button.dataset.code).toLowerCase() === code.toLowerCase()
  );

const setButtonState = (code, state) => {
  matchingButtons(code).forEach((button) => {
    const label = button.querySelector('[data-linea-coupon-button-label]') || button;
    if (!button.dataset.defaultLabel) button.dataset.defaultLabel = label.textContent.trim();
    if (!button.dataset.defaultAriaLabel) button.dataset.defaultAriaLabel = button.getAttribute('aria-label') || '';

    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-applied', state === 'applied');
    button.disabled = state === 'loading' || state === 'applied';
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    if (state === 'loading') {
      label.textContent = '適用中…';
      button.setAttribute('aria-label', `クーポン${code}を適用中`);
    }
    if (state === 'applied') {
      label.textContent = '適用済み';
      button.setAttribute('aria-label', `クーポン${code}を適用済み`);
    }
    if (state === 'idle') {
      label.textContent = button.dataset.defaultLabel;
      if (button.dataset.defaultAriaLabel) button.setAttribute('aria-label', button.dataset.defaultAriaLabel);
      else button.removeAttribute('aria-label');
    }
  });
};

const announce = (code, message) => {
  document.querySelectorAll('[data-linea-coupon-status]').forEach((status) => {
    const statusCode = normalizeCode(status.dataset.code);
    if (!statusCode || statusCode.toLowerCase() === code.toLowerCase()) status.textContent = message;
  });
};

const currentDiscountCodes = () => {
  const uniqueCodes = new Map();
  document.querySelectorAll('.cart-discount__pill[data-discount-code]').forEach((pill) => {
    const code = normalizeCode(pill.dataset.discountCode);
    if (code) uniqueCodes.set(code.toLowerCase(), code);
  });
  return Array.from(uniqueCodes.values());
};

const cartSectionIds = () => {
  const ids = new Set();
  document
    .querySelectorAll('cart-items-component[data-section-id], cart-discount-component[data-section-id]')
    .forEach((component) => {
      if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
    });
  return Array.from(ids);
};

const emitResult = (detail) => {
  document.dispatchEvent(new CustomEvent(RESULT_EVENT, { detail }));
};

const persistPending = (code) => {
  try {
    window.sessionStorage.setItem(PENDING_KEY, code);
  } catch (error) {}
};

const clearPending = () => {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch (error) {}
};

const readPending = () => {
  try {
    return normalizeCode(window.sessionStorage.getItem(PENDING_KEY));
  } catch (error) {
    return '';
  }
};

const applyCoupon = async (code, { silent = false } = {}) => {
  code = normalizeCode(code);
  if (!code || activeRequest) return;

  const existingCodes = currentDiscountCodes();
  if (existingCodes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
    clearPending();
    setButtonState(code, 'applied');
    announce(code, code.toUpperCase() === 'LINEA10' ? '500円OFFクーポンを適用済みです。' : `${code}を適用済みです。`);
    emitResult({ code, status: 'applied' });
    return;
  }

  setButtonState(code, 'loading');
  if (!silent) announce(code, 'クーポンを適用しています…');

  const sectionIds = cartSectionIds();
  const body = {
    discount: [...existingCodes, code].join(','),
    sections_url: window.location.pathname,
  };
  if (sectionIds.length) body.sections = sectionIds;

  activeRequest = fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }));

  try {
    const response = await activeRequest;
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    const data = await response.json();
    const discount = Array.isArray(data.discount_codes)
      ? data.discount_codes.find((item) => normalizeCode(item.code).toLowerCase() === code.toLowerCase())
      : null;

    document.dispatchEvent(new DiscountUpdateEvent(data, 'linea-coupon-apply'));

    if (discount?.applicable === true) {
      clearPending();
      setButtonState(code, 'applied');
      announce(code, code.toUpperCase() === 'LINEA10' ? '500円OFFクーポンを適用しました。' : `${code}を適用しました。`);
      emitResult({ code, status: 'applied', data });
      return;
    }

    if (Number(data.item_count || 0) === 0) {
      persistPending(code);
      setButtonState(code, 'idle');
      announce(code, 'クーポンを保存しました。商品追加後に自動で適用します。');
      emitResult({ code, status: 'pending', data });
      return;
    }

    clearPending();
    setButtonState(code, 'idle');
    announce(code, 'このクーポンは現在のカートでは利用できません。');
    emitResult({ code, status: 'error', data });
  } catch (error) {
    setButtonState(code, 'idle');
    if (!silent) announce(code, 'クーポンを適用できませんでした。時間をおいてお試しください。');
    emitResult({ code, status: 'error', error });
  } finally {
    activeRequest = null;
  }
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-linea-coupon-apply]');
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  applyCoupon(button.dataset.code);
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  const pendingCode = readPending();
  if (pendingCode) window.setTimeout(() => applyCoupon(pendingCode, { silent: true }), 80);
});

const retryPendingCoupon = async () => {
  const pendingCode = readPending();
  if (!pendingCode) return;

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    if (Number(cart.item_count || 0) > 0) applyCoupon(pendingCode, { silent: true });
  } catch (error) {}
};

retryPendingCoupon();
